import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { Data } from '.generated/templates/Feature.Data.model';
import { RichTextField, TextField } from '@sitecore-jss/sitecore-jss-react';
import Button from 'components/helpers/Button';
import { classnames } from 'tailwindcss-classnames';
import RichTextA11yWrapper from '../RichTextA11yWrapper';
import { getEnumValue } from 'lib/util/enum';
import {
  ValidHeadingLevels,
  ValidHeadingSizes,
  setHeadingSize,
  VideoSides,
} from 'lib/sitecore/component-params';
import { env } from 'lib/env';

export type VideoItemProps = Sitecore.Override.ItemEx &
  Data.Video.Fields.VideoItem & {
    videoTitleHeadlineLevel?: Sitecore.Override.ItemEx;
    videoTitleHeadlineSizing?: Sitecore.Override.ItemEx;
    videoSide?: Sitecore.Override.ItemEx;
    hideVideoContent?: boolean;
  };

const VideoItem = ({
  fields,
  videoTitleHeadlineLevel,
  videoTitleHeadlineSizing,
  videoSide,
  hideVideoContent,
}: VideoItemProps): JSX.Element => {
  if (!fields) {
    return <></>;
  }

  const videoHeading = videoTitleHeadlineLevel
    ? (getEnumValue(videoTitleHeadlineLevel) as ValidHeadingLevels)
    : 'h2';
  const videoHeadingSize = videoTitleHeadlineSizing
    ? (getEnumValue(videoTitleHeadlineSizing) as ValidHeadingSizes)
    : 'l';
  const videoSideValue = videoSide ? (getEnumValue(videoSide) as VideoSides) : 'right';
  const videoContainerClasses =
    videoSideValue === 'left'
      ? classnames('video-container', 'lg:flex-row-reverse')
      : classnames('video-container');

  const videoEmbedUrl = (() => {
    if (!fields?.videoEmbedUrl?.value) {
      return '';
    }
    const targetUri = new URL(fields.videoEmbedUrl?.value ?? '');
    if (!targetUri) {
      return '';
    }
    const sourceUri = new URL(env.PUBLIC_URL);
    const appendQuery =
      (targetUri.search?.length ?? 0) > 1 ? `&${targetUri.search.substring(1)}` : '';
    return `${targetUri.origin}${targetUri.pathname}?enablejsapi=1&origin=${
      sourceUri?.origin ?? ''
    }${appendQuery ? appendQuery : ''}`;
  })();

  return (
    <div data-component="authorable/general/videoitem" className={classnames('h-full', 'sm:mb-8')}>
      {/* We need left option in Sitecore for this item, by default the video will align right */}
      {/* <div className={classnames('video-container', 'video-align-left')}> */}
      <div className={videoContainerClasses}>
        {hideVideoContent ? (
          <></>
        ) : (
          <div className={classnames('video-content')}>
            <Text
              className={classnames(
                setHeadingSize(videoHeadingSize),
                'text-theme-heading-text',
                'pb-3'
              )}
              field={fields.videoTitle as TextField}
              tag={videoHeading}
            />
            <RichTextA11yWrapper
              className={classnames(
                'pb-6',
                'text-base',
                'leading-3',
                'sm:text-body',
                'sm:leading-5'
              )}
              field={fields.videoDescription as RichTextField}
            />
            <div className={classnames('mt-auto')}>
              <Button
                className={classnames('w-full', 'text-center', 'sm:w-fit', 'text-left')}
                linkField={fields.cta}
              />
              <Button
                linkField={fields.cta2}
                variant="secondary"
                className={classnames('w-full', 'text-center', 'sm:w-fit', 'text-left', 'sm:ml-3')}
              />
            </div>
          </div>
        )}

        <div className={classnames('video-item')}>
          <div className={classnames('video-wrapper')}>
            <iframe
              className={classnames('video-iframe')}
              title={fields.videoTitle?.value}
              src={videoEmbedUrl}
              referrerPolicy="strict-origin-when-cross-origin"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoItem;
